import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useFocusEffect } from "@react-navigation/native";

import {
    DEFAULT_PRODUCT_SETTINGS,
    ProductSettings,
} from "../../components/settings/productSettings.module";
import {
    getProductSettings,
    resetProductSettings,
    saveProductSettings,
} from "../../utils/productSettingsStorage";

export interface UseProductSettingsResult {
    settings: ProductSettings;
    loading: boolean;
    error: string | null;
    save: (next: ProductSettings) => Promise<ProductSettings>;
    reset: () => Promise<ProductSettings>;
    reload: () => Promise<ProductSettings>;
}

export interface UseProductSettingsOptions {
    /** Re-read storage every time the screen regains focus. */
    reloadOnFocus?: boolean;
}

const STORAGE_ERROR = "Unable to read the product settings. Showing the default values.";

/**
 * Owns one copy of the settings. Used directly by the provider and, when no
 * provider is mounted, by each consumer of useProductSettings.
 */
const useProductSettingsState = (enabled: boolean): UseProductSettingsResult => {
    const [settings, setSettings] = useState<ProductSettings>(DEFAULT_PRODUCT_SETTINGS);
    const [loading, setLoading] = useState<boolean>(enabled);
    const [error, setError] = useState<string | null>(null);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const reload = useCallback(async () => {
        if (!enabled) {
            return settings;
        }
        setLoading(true);
        try {
            const stored = await getProductSettings();
            if (mounted.current) {
                setSettings(stored);
                setError(null);
            }
            return stored;
        } catch {
            if (mounted.current) {
                setError(STORAGE_ERROR);
            }
            return DEFAULT_PRODUCT_SETTINGS;
        } finally {
            if (mounted.current) {
                setLoading(false);
            }
        }
        // `settings` is only read on the disabled path, where it never changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    useEffect(() => {
        reload();
    }, [reload]);

    const save = useCallback(async (next: ProductSettings) => {
        const saved = await saveProductSettings(next);
        if (mounted.current) {
            setSettings(saved);
            setError(null);
        }
        return saved;
    }, []);

    const reset = useCallback(async () => {
        const defaults = await resetProductSettings();
        if (mounted.current) {
            setSettings(defaults);
            setError(null);
        }
        return defaults;
    }, []);

    return useMemo(
        () => ({ settings, loading, error, save, reset, reload }),
        [settings, loading, error, save, reset, reload],
    );
};

const ProductSettingsContext = createContext<UseProductSettingsResult | null>(null);

/**
 * Optional. Mount it above the navigator so every screen shares one in-memory
 * copy and picks up a Save immediately, without its own storage read.
 */
export const ProductSettingsProvider = ({ children }: { children: React.ReactNode }) => {
    const value = useProductSettingsState(true);
    return React.createElement(ProductSettingsContext.Provider, { value }, children);
};

export const useProductSettings = (options?: UseProductSettingsOptions): UseProductSettingsResult => {
    const shared = useContext(ProductSettingsContext);
    // Without a provider each consumer keeps its own copy; with one, the local
    // state is left idle so storage is read a single time per app session.
    const local = useProductSettingsState(!shared);
    const value = shared ?? local;

    const { reload } = value;
    const reloadOnFocus = options?.reloadOnFocus ?? false;

    useFocusEffect(
        useCallback(() => {
            if (reloadOnFocus) {
                reload();
            }
        }, [reloadOnFocus, reload]),
    );

    return value;
};

export default useProductSettings;
